import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Badge,
  Button,
  Copy,
  Dropdown,
  Edit,
  Input,
  Menu,
  Refresh as RefreshIcon,
  Table,
  Trash,
} from '@react-apps/design-system';
import type { DropdownMenuEntry } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { Institution } from '../../domain/entities/content.entity';
import {
  useArchiveInstitution,
  useInstitutions,
  useRestoreInstitution,
} from '../hooks/use-institutions';
import { Mono, notifyDone, notifyFailed } from '../display';
import { Pagination } from './Pagination';
import {
  InstitutionFormModal,
  translateInstitutionError,
} from './InstitutionFormModal';
import { ConfirmModal } from './ConfirmModal';
import { MergeInstitutionModal } from './MergeInstitutionModal';
import { useCanMergeInstitutions } from '../hooks/use-content-permissions';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;
const SEARCH_PARAM = 'search';
const INCLUDE_DELETED_PARAM = 'includeDeleted';
const IN_HOUSE_PARAM = 'isInHouse';
/** Deep link da importação: `?new=1&initials=X&uf=SP&name=…`. */
const NEW_PARAM = 'new';

/**
 * Gerência de instituições. Espelha o `SubjectsPanel` (página, busca e filtros
 * na URL), com duas diferenças que vêm da natureza da entidade:
 *
 * - A coluna **Provas** (`examCount`) não é enfeite: é a precondição de
 *   arquivar e o alcance de renomear. Com prova apontando, o botão de arquivar
 *   fica desabilitado exibindo o número.
 * - "Excluir" se chama **arquivar**: a linha sai das ofertas (seletor de prova,
 *   CSV modelo, prova nova) mas continua resolvendo o rótulo `(SIGLA-UF) NOME`
 *   das provas que já existem — e volta por "Restaurar".
 */
export function InstitutionsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const includeDeleted = searchParams.get(INCLUDE_DELETED_PARAM) === 'true';
  const inHouseParam = searchParams.get(IN_HOUSE_PARAM);
  const isInHouse = inHouseParam === null ? undefined : inHouseParam === 'true';
  const [searchInput, setSearchInput] = useState(search);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Institution | null>(null);
  const [archiving, setArchiving] = useState<Institution | null>(null);
  const [merging, setMerging] = useState<Institution | null>(null);

  const { data, isLoading, refetch, isFetching } = useInstitutions({
    page,
    perPage: PER_PAGE,
    search,
    includeDeleted,
    isInHouse,
  });
  const canMerge = useCanMergeInstitutions();
  const archive = useArchiveInstitution();
  const restore = useRestoreInstitution();

  // O deep link do erro de importação (`NOT_FOUND` → "Cadastrar instituição")
  // abre o modal já preenchido com o que estava na planilha. O `?new=1` sai da
  // URL na abertura para "Voltar" não reabrir o modal.
  const openNew = searchParams.get(NEW_PARAM) === '1';
  const prefill = {
    initials: searchParams.get('initials') ?? undefined,
    uf: searchParams.get('uf') ?? undefined,
    name: searchParams.get('name') ?? undefined,
  };
  useEffect(() => {
    if (!openNew) return;
    setIsCreateOpen(true);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete(NEW_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [openNew, setSearchParams]);

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };

  // Cada mudança de filtro reseta a página no MESMO setSearchParams: duas
  // chamadas no mesmo tick partem do mesmo `location.search` e a segunda
  // sobrescreve a primeira (ver use-page-query-param.ts).
  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (trimmed) params.set(SEARCH_PARAM, trimmed);
        else params.delete(SEARCH_PARAM);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const toggleIncludeDeleted = (checked: boolean) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (checked) params.set(INCLUDE_DELETED_PARAM, 'true');
        else params.delete(INCLUDE_DELETED_PARAM);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const setInHouseFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set(IN_HOUSE_PARAM, value);
        else params.delete(IN_HOUSE_PARAM);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const confirmArchive = () => {
    if (!archiving) return;
    archive.mutate(archiving.id, {
      onSuccess: () => {
        setArchiving(null);
        archive.reset();
        notifyDone('Instituição arquivada — sai do seletor e do CSV modelo');
      },
    });
  };

  const handleRestore = (row: Institution) => {
    restore.mutate(row.id, {
      onSuccess: () => notifyDone('Instituição restaurada'),
      // A mensagem traduzida entra como o próprio "erro": `getApiErrorMessage`
      // deixa string passar, então é ela que aparece no toast — e não a
      // mensagem de domínio em inglês que vem no corpo da resposta.
      onError: (err) =>
        notifyFailed(
          translateInstitutionError(err, 'Falha ao restaurar a instituição'),
          'Falha ao restaurar a instituição',
        ),
    });
  };

  const columns: ColumnDef<Institution>[] = [
    // A UF abre a tabela: é o primeiro eixo pelo qual se procura uma
    // instituição (o `InstitutionSelect` pede UF antes da sigla pelo mesmo
    // motivo), e ler `SP` na frente de `UNICAMP` já monta o rótulo do CSV na
    // cabeça de quem está conferindo uma planilha.
    {
      key: 'uf',
      header: 'UF',
      render: (row) => (
        <span className="text-(--color-text-secondary) font-medium">
          {row.uf}
        </span>
      ),
    },
    {
      key: 'initials',
      header: 'Sigla',
      render: (row) => (
        <span className="font-semibold">
          <Mono>{row.initials}</Mono>
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Nome',
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-(--color-text-primary)">{row.name}</span>
          {row.isInHouse && <Badge variant="info">Própria</Badge>}
        </div>
      ),
    },
    {
      key: 'id',
      header: 'ID',
      // Corte por CSS, não na string — o mesmo padrão (e o mesmo motivo) do
      // `InstitutionCell` em `display.tsx`: o `title` cobre quem quer ler, e
      // "Copiar o ID" no menu entrega o valor inteiro. Com `Mono` cru o UUID
      // usava `break-all` e quebrava em duas linhas, o que dobrava a altura de
      // toda linha da tabela e era o que empurrava a largura para o scroll.
      // Clicar na célula copia — é o gesto que a pessoa já tenta quando vê um
      // id truncado, e evita abrir o menu para uma ação de uma palavra.
      // `<button>` e não `<span onClick>`: teclado e leitor de tela precisam
      // alcançar isso, e o `title` diz o que o clique faz (o truncamento já
      // esconde o valor).
      render: (row) => (
        <button
          type="button"
          title={`${row.id} — clique para copiar`}
          onClick={(e) => {
            e.stopPropagation();
            void copyId(row);
          }}
          className="hover:text-(--color-text-primary) block max-w-[11rem] cursor-pointer truncate whitespace-nowrap text-left font-mono text-xs underline decoration-dotted underline-offset-2"
        >
          {row.id}
        </button>
      ),
    },
    {
      key: 'examCount',
      header: 'Provas',
      render: (row) => row.examCount,
    },
    {
      key: 'status',
      header: 'Situação',
      render: (row) =>
        row.deletedAt ? (
          <Badge variant="warning">Arquivada</Badge>
        ) : (
          <Badge variant="success">Ativa</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex justify-end">
          {/* `portal`: a `Table` embrulha o corpo num `overflow-auto`, e sem
              isso o menu era recortado pela borda — o container ganhava uma
              barra de scroll para caber um menu que ninguém queria rolar. */}
          <Dropdown
            portal
            align="right"
            items={actionsFor(row)}
            trigger={kebab(row)}
          />
        </div>
      ),
    },
  ];

  /**
   * As ações da linha, em ordem de frequência de uso.
   *
   * **Menu em vez de ícones soltos** porque eram quatro, e dois deles
   * (arquivar, mesclar) são destrutivos e precisam de mais do que um símbolo
   * para serem entendidos. O menu é texto: o motivo de "Arquivar" estar
   * desabilitado cabe no próprio rótulo, em vez de exigir hover num botão
   * `disabled` — que não dispara evento de mouse e por isso nem mostrava o
   * tooltip em alguns navegadores.
   */
  const actionsFor = (row: Institution): DropdownMenuEntry[] => {
    const arquivada = !!row.deletedAt;
    const temProva = row.examCount > 0;

    return [
      {
        id: 'copy-label',
        // O rótulo do CSV. Não repito `(SIGLA-UF)` aqui: as duas primeiras
        // colunas da tabela já são UF e sigla, então o valor está à vista na
        // mesma linha.
        label: 'Copiar rótulo',
        icon: <Copy size="sm" />,
        onClick: () => void copyLabel(row),
      },
      {
        id: 'copy-id',
        label: 'Copiar ID',
        icon: <Copy size="sm" />,
        onClick: () => void copyId(row),
      },
      ...(arquivada
        ? [
            {
              id: 'restore',
              label: 'Restaurar',
              icon: <RefreshIcon size="sm" />,
              disabled: restore.isPending,
              onClick: () => handleRestore(row),
            },
          ]
        : [
            {
              id: 'edit',
              label: 'Editar',
              icon: <Edit size="sm" />,
              onClick: () => setEditing(row),
            },
          ]),
      // A mescla só faz algo com prova apontando: com zero, arquivar já
      // resolve e está habilitado. É por isso que ela aparece exatamente onde
      // o arquivar está bloqueado — inclusive em linha arquivada, que pode ter
      // ficado com prova de antes da guarda existir.
      ...(canMerge && temProva
        ? [
            {
              id: 'merge',
              label: 'Mesclar',
              icon: <ArrowRight size="sm" />,
              onClick: () => setMerging(row),
            },
          ]
        : []),
      ...(arquivada
        ? []
        : [
            {
              id: 'archive',
              label: 'Arquivar',
              // Desabilitado e SEM motivo na tela, por decisão de 2026-09-17:
              // o rótulo fica curto. Quem quer saber por que não dá olha a
              // coluna "Provas" da mesma linha, e o 409 `INSTITUTION_IN_USE`
              // traz o número se alguém chegar lá por outro caminho.
              icon: <Trash size="sm" />,
              disabled: temProva,
              onClick: () => setArchiving(row),
            },
          ]),
    ];
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={applySearch} className="flex items-end gap-2">
          <Input
            label="Buscar"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Sigla ou nome…"
          />
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Button onClick={() => setIsCreateOpen(true)}>
            Cadastrar instituição
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => toggleIncludeDeleted(e.target.checked)}
          />
          Incluir arquivadas
        </label>
        <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
          Conteúdo próprio
          <select
            className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) rounded border px-2 py-1 text-sm"
            value={inHouseParam ?? ''}
            onChange={(e) => setInHouseFilter(e.target.value)}
          >
            <option value="">Todas</option>
            <option value="true">Só as nossas</option>
            <option value="false">Só as de fora</option>
          </select>
        </label>
      </div>

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhuma instituição encontrada."
        striped
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="instituição"
        onPageChange={setPage}
      />

      <InstitutionFormModal
        open={isCreateOpen}
        prefill={prefill}
        onClose={() => setIsCreateOpen(false)}
        onOpenExisting={(institutionId, initials) => {
          setIsCreateOpen(false);
          const existing = data?.items.find((i) => i.id === institutionId);
          // Já visível e viva? abre direto para edição. Se estiver arquivada (ou
          // fora desta página), o caminho é a listagem filtrada pela sigla com
          // as arquivadas à vista — é lá que o botão "Restaurar" está, e editar
          // uma arquivada não é oferecido de propósito.
          if (existing && !existing.deletedAt) {
            setEditing(existing);
            return;
          }
          setSearchInput(initials);
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.set(SEARCH_PARAM, initials);
              params.set(INCLUDE_DELETED_PARAM, 'true');
              writePageParam(params, 1);
              return params;
            },
            { replace: true },
          );
        }}
      />
      <InstitutionFormModal
        open={!!editing}
        institution={editing}
        onClose={() => setEditing(null)}
      />
      <MergeInstitutionModal
        source={merging}
        onClose={() => setMerging(null)}
      />
      <ConfirmModal
        open={!!archiving}
        onClose={() => {
          setArchiving(null);
          archive.reset();
        }}
        onConfirm={confirmArchive}
        title="Arquivar instituição"
        description={`Arquivar "${archiving?.initials}"? Ela sai do seletor de instituição e do CSV modelo, e deixa de ser aceita em prova nova. O rótulo continua resolvendo nas provas antigas, e dá para restaurar depois.`}
        confirmLabel="Arquivar"
        loading={archive.isPending}
        error={
          archive.error
            ? translateInstitutionError(
                archive.error,
                'Falha ao arquivar a instituição',
              )
            : undefined
        }
      />
    </div>
  );
}

/**
 * O gatilho do menu. Mesmo desenho do `QuestionPreviewCard`: um `⋯` com
 * `aria-label` nomeando a linha, para leitor de tela e para o teste não
 * dependerem de ordem na tabela.
 */
function kebab(row: Institution) {
  return (
    <span
      role="img"
      aria-label={`Ações de ${row.initials}`}
      title="Ações"
      className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) flex h-7 w-7 items-center justify-center rounded"
    >
      <Menu size="sm" />
    </span>
  );
}

/** O UUID cru — o que se cola num script ou numa chamada de API. */
async function copyId(row: Institution): Promise<void> {
  try {
    await navigator.clipboard.writeText(row.id);
    notifyDone('ID copiado');
  } catch {
    notifyFailed(null, 'Não foi possível copiar o ID');
  }
}

/** `(SIGLA-UF) NOME` — a mesma gramática de `formatInstitutionLabel`. */
async function copyLabel(row: Institution): Promise<void> {
  const label = `(${row.initials}-${row.uf}) ${row.name}`;
  try {
    await navigator.clipboard.writeText(label);
    notifyDone('Rótulo copiado');
  } catch {
    notifyFailed(null, 'Não foi possível copiar o rótulo');
  }
}
